import React, { useCallback, useContext, useEffect, useState } from 'react';
import { CloseCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  Row,
  Select,
  Switch,
} from 'antd';
import { YEAR } from 'antd/es/date-picker/generatePicker/constant';
import dayjs, { Dayjs } from 'dayjs';

import SecurityGroupSelectorDialog from '@totem/components/documents/dialogs/securityGroupSelectorDialog';
import DocumentsContext from '@totem/components/documents/report/documentsContext';
import { DocumentReference } from '@totem/components/documents/types';
import { referenceFields } from '@totem/components/documents/utilities';
import ModalFooter from '@totem/components/ui_components/dialogs/ModalFooter';
import { USER_ROLES } from '@totem/components/userContext/accountUtilities';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { EligibleSecurityGroup } from '@totem/types/user';
import { DOCUMENTS_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';

const styles = {
  form: {
    width: '100%',
  },
  formItem: {
    paddingBottom: '0',
    marginBottom: '1rem',
  },
  iconButton: {
    margin: '0 2px',
  },
  removeIcon: {
    color: '#1890ff',
  },
};

type Props = {
  onClose: (refresh: boolean) => void;
  // Overrides the container's reference, e.g. when the user picks what to link the document to.
  reference?: DocumentReference;
};

const WebLinkPanel = ({ onClose, reference }: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const [webLink, setWebLink] = useState<string>('');
  const [webLinkDirty, setWebLinkDirty] = useState<boolean>(false);
  const [linkTitle, setLinkTitle] = useState<string>('');
  const [linkTitleDirty, setLinkTitleDirty] = useState<boolean>(false);
  const { documentReference, documentReferenceInternal } =
    useContext(DocumentsContext);
  const [loading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [internalOnly, setInternalOnly] = useState<boolean>(
    documentReferenceInternal,
  );
  const defaultExpirationDate = dayjs().add(10, YEAR);
  const [selectedExpirationDate, setSelectedExpirationDate] = useState<Dayjs>(
    defaultExpirationDate,
  );
  const [selectedSecurityGroups, setSelectedSecurityGroups] = useState<
    EligibleSecurityGroup[]
  >([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedRole, setSelectedRole] = useState<number>(0);
  const [showSecurityGroupsDialog, setShowSecurityGroupsDialog] =
    useState<boolean>(false);
  const [showTagEntry, setShowTagEntry] = useState<boolean>(false);
  const [tmpTag, setTmpTag] = useState<string>('');
  useEffect(() => {
    setInternalOnly(documentReferenceInternal);
  }, [documentReferenceInternal]);

  const sendAddWebLink = useCallback(
    async (payload: any) => {
      if (!isSending) {
        fetch(`${DOCUMENTS_ENDPOINT}/upload/link`, {
          method: 'POST',
          headers: new Headers({
            Authorization: `Bearer ${getApiToken()}`,
          }),
          body: JSON.stringify(payload),
        }).then((res) => {
          setIsSending(false);
          const success = CheckResponseShowError(res);
          if (success) {
            onClose(true);
          }
        });
      }
    }, // End async
    [],
  );

  const isFormValid = () => {
    if (!linkTitle.trim()) return false;
    try {
      const parsed = new URL(webLink);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const handleSubmit = () => {
    if (isFormValid()) {
      const payload = {
        ...referenceFields(reference ?? documentReference),
        internalOnly,
        displayName: linkTitle,
        storagePath: webLink,
        expirationDate: selectedExpirationDate.toISOString(),
        securityGroups: selectedSecurityGroups.map((item) => item.name),
        tags: selectedTags,
      };

      sendAddWebLink(payload);
    }
  };

  let webLinkError: string | null = null;
  if (webLinkDirty) {
    if (!webLink.trim()) {
      webLinkError = 'URL is required';
    } else {
      try {
        const parsed = new URL(webLink);
        if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
          webLinkError = 'URL must start with http:// or https://';
        }
      } catch {
        webLinkError = 'Please enter a valid URL';
      }
    }
  }

  const linkTitleError: string | null =
    linkTitleDirty && !linkTitle.trim() ? 'Title is required' : null;

  const roleOptions = USER_ROLES.filter(
    (role) => role.value <= userProfile.organization.role,
  ).map((role) => ({
    value: role.value,
    label: role.label,
  }));

  return (
    <div>
      <Form layout="vertical" style={styles.form} onFinish={handleSubmit}>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label="Web URL"
              validateStatus={webLinkError ? 'error' : undefined}
              help={webLinkError}
            >
              <Input
                placeholder="URL"
                onChange={(evt) => {
                  setWebLinkDirty(true);

                  setWebLink(evt.target.value);
                }}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Title"
              validateStatus={linkTitleError ? 'error' : undefined}
              help={linkTitleError}
            >
              <Input
                placeholder="Title"
                onChange={(evt) => {
                  setLinkTitleDirty(true);

                  setLinkTitle(evt.target.value);
                }}
              />
            </Form.Item>
            {/* Date Picker */}
            <Form.Item name="expirationDate" label="Expiration Date">
              <DatePicker
                allowClear={false}
                value={selectedExpirationDate}
                onChange={(time) => setSelectedExpirationDate(time)}
                format={'L LT'}
                style={{ width: '200px' }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={
                <span>
                  Tags&nbsp;&nbsp;
                  <Button
                    icon={<PlusCircleOutlined />}
                    onClick={() => setShowTagEntry(true)}
                    disabled={showTagEntry}
                  />{' '}
                </span>
              }
              style={styles.formItem}
            >
              {showTagEntry && (
                <Flex justify="flex-start">
                  <Input
                    value={tmpTag}
                    onChange={(evt) => setTmpTag(evt.target.value)}
                    onBeforeInput={(evt) => {
                      const nativeEvent = evt.nativeEvent as InputEvent;
                      if (nativeEvent.data && nativeEvent.data.includes(',')) {
                        evt.preventDefault(); // Prevent inserting the character
                      }
                    }}
                  />
                  <Button onClick={() => setShowTagEntry(false)}>Cancel</Button>
                  <Button
                    onClick={() => {
                      setSelectedTags((prevState) => [...prevState, tmpTag]);

                      setTmpTag('');

                      setShowTagEntry(false);
                    }}
                  >
                    Add
                  </Button>
                </Flex>
              )}
              {selectedTags.map((chk) => (
                <div key={chk}>
                  <CloseCircleOutlined
                    onClick={() => {
                      setSelectedTags((prevState) =>
                        prevState.filter((tag) => tag !== chk),
                      );
                    }}
                  />
                  &nbsp;&nbsp;{chk}
                </div>
              ))}
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Minimum role required to view document"
              style={styles.formItem}
            >
              <Select
                onChange={(role) => setSelectedRole(role)}
                value={selectedRole}
                options={roleOptions}
              />
            </Form.Item>
            {permissions.internal.hasPermissions(userProfile) && (
              <Form.Item label="Internal Only" style={styles.formItem}>
                <Switch
                  defaultChecked={internalOnly}
                  value={internalOnly}
                  onChange={(isChecked) => setInternalOnly(isChecked)}
                />
              </Form.Item>
            )}
          </Col>
          <Col span={12}>
            <Form.Item
              label={
                <span>
                  Additional Security&nbsp;&nbsp;
                  <Button
                    icon={<PlusCircleOutlined />}
                    onClick={() => setShowSecurityGroupsDialog(true)}
                  />{' '}
                </span>
              }
              style={styles.formItem}
            >
              {selectedSecurityGroups.length > 0 && (
                <div style={{ color: '#FF0000', fontSize: 'small' }}>
                  (user must be a member of one of the following groups to
                  groups to view):
                </div>
              )}
              {selectedSecurityGroups.map((chk) => (
                <div key={chk.id}>
                  <CloseCircleOutlined
                    onClick={() => {
                      setSelectedSecurityGroups((prevState) =>
                        prevState.filter((group) => group !== chk),
                      );
                    }}
                  />
                  &nbsp;&nbsp;
                  {chk.displayName}
                </div>
              ))}
            </Form.Item>
          </Col>
        </Row>
      </Form>
      {showSecurityGroupsDialog && (
        <SecurityGroupSelectorDialog
          onClose={() => setShowSecurityGroupsDialog(false)}
          onSubmit={(securityGroup) => {
            if (selectedSecurityGroups.includes(securityGroup)) {
              const updatedGroups = selectedSecurityGroups.filter(
                (chk) => chk !== securityGroup,
              );
              setSelectedSecurityGroups(updatedGroups);
            } else {
              setSelectedSecurityGroups((prevItems) => [
                ...prevItems,
                securityGroup,
              ]);
            }
            setShowSecurityGroupsDialog(false);
          }}
          visible={showSecurityGroupsDialog}
        />
      )}
      <ModalFooter>
        <Button onClick={() => onClose(false)}>Close</Button>
        <Button
          type="primary"
          onClick={handleSubmit}
          disabled={!isFormValid() && !loading}
        >
          Save
        </Button>
      </ModalFooter>
    </div>
  );
};

export default WebLinkPanel;
